
import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Upload } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

interface ProfileImageUploadProps {
  profileImageUrl: string | null;
  initials: string;
  onFileSelect: (file: File) => void;
}

const ProfileImageUpload = ({ 
  profileImageUrl, 
  initials,
  onFileSelect 
}: ProfileImageUploadProps) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(profileImageUrl);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    onFileSelect(file);
    
    // Create a preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="flex flex-col items-center mb-4">
      <Avatar className="h-24 w-24 border-2 border-gray-200 mb-4">
        {previewUrl ? (
          <AvatarImage src={previewUrl} alt="Profile preview" />
        ) : (
          <AvatarFallback className="text-xl">{initials}</AvatarFallback>
        )}
      </Avatar>
      
      <input
        type="file"
        id="profileImage"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        className="hidden"
      />
      
      <Button
        type="button"
        variant="outline"
        onClick={() => fileInputRef.current?.click()}
        className="flex items-center gap-2"
      >
        <Upload className="h-4 w-4" />
        {previewUrl !== profileImageUrl ? "Change Image" : "Upload Image"}
      </Button>
      
      {selectedFile && (
        <p className="text-sm text-gray-500 mt-2">
          Selected: {selectedFile.name}
        </p>
      )}
    </div>
  );
};

export default ProfileImageUpload;
